// The gather figure (R1123, `ui-map-interface.md` §6.1.4): the number that
// states what one wall second put into the hold.
//
// Card 09 of mockup round 055 prints a figure beside the readout lanes of a
// Dredger at work. This is the accumulator behind it. It is NOT a gather
// look: every player gets it under every look, so it reads `GatherState` and
// never a theme.
//
// **The number is real.** It is the units that came into the hold, read off
// the hold delta of the own fleets frame by frame. The card's `+30` was a
// placeholder and its own caption says an honest build prints the true lot.
//
// **One figure a wall second for one STACK, and the stack is the FIELD.** The
// tracker names the site of every cut, so the deltas of every own hull that
// cuts one deposit are summed against that deposit, and the field prints ONE
// figure each wall second at the hull that LEADS its cuts. The
// `separation_px` circle of the stack digit is the other rule, and it was
// refused: it needs its own walk, it can group two Dredgers that work two
// different deposits, and two commodities can never share one field.
//
// **The floor prints and the fraction carries.** A wire that rounds a hold
// away loses nothing over a visit, and a second whose floor is 0 prints
// nothing at all. A hull that leaves the stack inside a second loses nothing
// either, because the sum is the field's and not the hull's.
//
// **The clock is WALL time.** The map runs at 30x on a core world and 120x on
// a local server, and a window paid in map seconds would print one hundred
// and twenty figures a second on one of them.
//
// **`FULL` does not wait for the second.** It fires on the rising edge of the
// hold against its cap, at most one for a field while a `FULL` of that field
// still stands, so the word and the lane flash of R1122 arrive together.
//
// This is a leaf (`leaves.test.ts`): it imports the generated pack and the
// tracker's types, and never an engine. **Nothing here allocates per frame**
// (R131): every figure is a record of a fixed-capacity pool, a field is a
// record built once and reused, and a walk writes into what it already owns.

import { GATHER_FIGURE } from '../gen/presentation';
import type { GatherCut } from './gather-state';

/** What one figure states. */
export const enum GatherFigureKind {
  /** The chip and `+N`: the units of one wall second. */
  Units = 0,
  /** The word `FULL`: the hold filled. */
  Full = 1,
}

/** ONE live figure, as the fleet layer packs it. */
export interface GatherFigureItem {
  kind: GatherFigureKind;
  /** The hull the figure rises off, so the layer may follow the mark. */
  fleetId: number;
  /** Where the figure stood when it was raised, in world px. */
  x: number;
  y: number;
  age: number;
  life: number;
  /** The units of a `Units` figure, and 0 on a `Full` one. */
  n: number;
  /** The commodity, for the ink and the chip. */
  resource: number;
  /** The cut was an R715 shaft: the hollow chip and the smaller figure. */
  shaft: boolean;
}

/** One field under the cut, and what its own hulls owe the next second. */
interface Field {
  id: number;
  /** The units this field has not printed yet, fraction and all. */
  sum: number;
  /** The hull that leads the field's cuts this frame, and where it stands. */
  leadId: number;
  x: number;
  y: number;
  resource: number;
  shaft: boolean;
  /** A cut of this field was noted this frame. */
  seen: boolean;
  /** Seconds since the last frame that noted one. */
  idle: number;
  /** A hull of this field filled its hold and the word has not gone out. */
  pendingFull: boolean;
  /** The clock the last `FULL` of this field went out at. */
  fullAt: number;
}

/** What one hull held, so the next frame can read the delta. */
interface Hull {
  /** The fleet id, so the frame's close walks the values alone. */
  id: number;
  hold: number;
  full: boolean;
  /** The frame this record was last noted on. */
  stamp: number;
}

const NEVER = -1e9;

export class GatherFigures {
  /** The pool. The live figures stand at the front. */
  private readonly pool: GatherFigureItem[] = [];
  private live = 0;

  private readonly fields = new Map<number, Field>();
  private readonly hulls = new Map<number, Hull>();

  /** The wall clock, and what is left of the present window. */
  private clock = 0;
  private window = 0;
  /** The step of this frame, in seconds of wall time. */
  private dt = 0;
  /** The frame counter that sweeps a hull nothing noted. */
  private frame = 0;

  constructor(private readonly cap: number = GATHER_FIGURE.maxFigures) {}

  /** How many figures stand. A test and the stats line read it. */
  get count(): number { return this.live; }

  /** The live figures. The array is the pool itself, so a reader walks
   *  `count` entries and keeps no reference. */
  items(): readonly GatherFigureItem[] { return this.pool; }

  /** Takes every figure off the water and forgets every hold (a season roll,
   *  a seek, a new hello). */
  clear(): void {
    this.live = 0;
    this.fields.clear();
    this.hulls.clear();
    this.clock = 0;
    this.window = 0;
  }

  /** Opens a frame. `dtSec` is WALL seconds, clamped by the caller. */
  begin(dtSec: number): void {
    this.dt = dtSec > 0 ? dtSec : 0;
    this.clock += this.dt;
    this.window += this.dt;
    this.frame++;
    for (let i = this.live - 1; i >= 0; i--) {
      const f = this.pool[i];
      f.age += this.dt;
      if (f.age > f.life) this.drop(i);
    }
    for (const f of this.fields.values()) {
      f.seen = false;
      f.idle += this.dt;
    }
  }

  /**
   * One cut of an OWN hull, once a frame.
   *
   * A cut the viewer cannot read the hold of is skipped: `holdUnits` is −1 on
   * a faction hull, and a contact never reaches the tracker at all (R1121).
   */
  note(c: GatherCut): void {
    if (!c.own || c.holdUnits < 0 || c.siteId === 0) return;
    const field = this.fieldOf(c);
    const hull = this.hullOf(c);
    // The delta. An unload drops the hold, which is no gain, so it floors at
    // nothing rather than eating the next second's units.
    if (c.holdUnits > hull.hold) field.sum += c.holdUnits - hull.hold;
    hull.hold = c.holdUnits;
    hull.stamp = this.frame;
    const full = c.cargoCap > 0 && c.holdUnits >= c.cargoCap;
    if (full && !hull.full) field.pendingFull = true;
    hull.full = full;
  }

  /**
   * Closes a frame: the word goes out at once, the number at the boundary of
   * the wall-second window, and a field nobody works any more leaves.
   */
  end(): void {
    const g = GATHER_FIGURE;
    const due = this.window >= g.everySec;
    if (due) this.window -= g.everySec;
    for (const f of this.fields.values()) {
      if (f.pendingFull && this.clock - f.fullAt >= g.fullLifeSec) {
        f.pendingFull = false;
        f.fullAt = this.clock;
        this.raise(GatherFigureKind.Full, f, 0, g.fullLifeSec);
      }
      if (due) {
        const n = Math.floor(f.sum);
        if (n >= 1) {
          f.sum -= n;
          this.raise(GatherFigureKind.Units, f, n,
            f.shaft ? g.shaftLifeSec : g.lifeSec);
        }
      }
      // A field nobody cuts any more is kept for one whole window, so its
      // remainder still reaches a figure, and then it goes.
      if (!f.seen && f.idle > g.everySec) this.fields.delete(f.id);
    }
    for (const h of this.hulls.values()) {
      if (h.stamp !== this.frame) this.hulls.delete(h.id);
    }
  }

  // -- the pool ---------------------------------------------------------------

  private raise(kind: GatherFigureKind, f: Field, n: number, life: number): void {
    // The cap drops the OLDEST figure, which is the one a reader has already
    // read, rather than refusing the number that just came aboard.
    if (this.live >= this.cap) this.drop(0);
    if (this.pool.length <= this.live) this.pool.push(blank());
    const it = this.pool[this.live++];
    it.kind = kind;
    it.fleetId = f.leadId;
    it.x = f.x;
    it.y = f.y;
    it.age = 0;
    it.life = life;
    it.n = n;
    it.resource = f.resource;
    it.shaft = f.shaft;
  }

  /** Takes one figure out by swapping the last live one into its slot. */
  private drop(i: number): void {
    const it = this.pool[i];
    const last = --this.live;
    if (i !== last) {
      this.pool[i] = this.pool[last];
      this.pool[last] = it;
    }
  }

  /** The field of one cut, built on its first frame and reused after it. */
  private fieldOf(c: GatherCut): Field {
    let f = this.fields.get(c.siteId);
    if (f === undefined) {
      f = {
        id: c.siteId, sum: 0, leadId: c.id, x: c.x, y: c.y,
        resource: c.resource, shaft: c.shaft, seen: false, idle: 0,
        pendingFull: false, fullAt: NEVER,
      };
      this.fields.set(c.siteId, f);
    }
    // The FIRST cut of the field this frame leads it, which is the tracker's
    // own pool order, so the figure stands at one hull of the stack and not
    // at a different one every second.
    if (!f.seen) {
      f.seen = true;
      f.idle = 0;
      f.leadId = c.id;
      f.x = c.x;
      f.y = c.y;
      f.resource = c.resource;
      f.shaft = c.shaft;
    }
    return f;
  }

  /** The hold record of one hull, built on the frame it is first noted. */
  private hullOf(c: GatherCut): Hull {
    let h = this.hulls.get(c.id);
    if (h === undefined) {
      // The first frame of a visit is the BASELINE and gains nothing: the
      // hold it arrives with was loaded somewhere else. A hull that arrives
      // already full carries that state too, so the word does not print for
      // a hold nothing filled here.
      h = {
        id: c.id,
        hold: c.holdUnits,
        full: c.cargoCap > 0 && c.holdUnits >= c.cargoCap,
        stamp: this.frame,
      };
      this.hulls.set(c.id, h);
    }
    return h;
  }
}

function blank(): GatherFigureItem {
  return {
    kind: GatherFigureKind.Units, fleetId: 0, x: 0, y: 0,
    age: 0, life: 1, n: 0, resource: 1, shaft: false,
  };
}
